import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as EnabledIcon,
  Cancel as DisabledIcon,
} from '@mui/icons-material';
import { api } from '../../utils/api';
import { useFeatureFlags } from '../../contexts/FeatureFlagContext';

interface FeatureFlag {
  flag_key: string;
  enabled: boolean;
  description: string;
  scope: string;
  metadata: Record<string, any> | null;
  tenant_id: string | null;
  updated_at: string;
  updated_by: string | null;
}

interface SubTabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function SubTabPanel(props: SubTabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

interface SystemManagementTabProps { initialSubTab?: number; lockedView?: 'flags' }

const SystemManagementTab: React.FC<SystemManagementTabProps> = ({ initialSubTab, lockedView }) => {
  const [subTab, setSubTab] = useState(lockedView === 'flags' ? 0 : (initialSubTab ?? 1));
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [systemHealth, setSystemHealth] = useState<any>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [healthLoaded, setHealthLoaded] = useState(false);
  const [filterScope, setFilterScope] = useState<string>('all');
  const [filterEnabled, setFilterEnabled] = useState<string>('all');
  
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    flag: FeatureFlag | null;
    newValue: boolean;
  }>({ open: false, flag: null, newValue: false });
  const [updating, setUpdating] = useState(false);

  // Refresh global FeatureFlagContext after admin toggles a flag
  const { refetchFlags } = useFeatureFlags();

  useEffect(() => {
    fetchFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFlags = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/feature-flags');
      const data = await response.json();
      if (data.success) {
        setFlags(data.flags);
      } else {
        setError('Failed to fetch feature flags');
      }
    } catch (err: any) {
      console.error('Error fetching flags:', err);
      setFlags([]);
      setError('Failed to load feature flags. Check connection and retry.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleClick = (flag: FeatureFlag) => {
    setConfirmDialog({
      open: true,
      flag,
      newValue: !flag.enabled,
    });
  };

  const handleConfirmToggle = async () => {
    if (!confirmDialog.flag) return;
    
    setUpdating(true);
    try {
      const response = await api.put(`/admin/feature-flags/${confirmDialog.flag.flag_key}`, {
        enabled: confirmDialog.newValue,
      });
      const data = await response.json();
      
      if (data.success) {
        setFlags(prev =>
          prev.map(f =>
            f.flag_key === confirmDialog.flag!.flag_key
              ? { ...f, enabled: confirmDialog.newValue, updated_at: new Date().toISOString() }
              : f
          )
        );
        // Refresh global FeatureFlagContext so user-facing surfaces pick up the change
        refetchFlags();
      }
    } catch (err) {
      console.error('Error updating flag:', err);
      setError('Failed to update feature flag');
    } finally {
      setUpdating(false);
      setConfirmDialog({ open: false, flag: null, newValue: false });
    }
  };

  const filteredFlags = flags.filter(flag => {
    const matchesSearch = flag.flag_key.toLowerCase().includes(searchQuery.toLowerCase()) ||
      flag.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesScope = filterScope === 'all' || flag.scope === filterScope;
    const matchesEnabled = filterEnabled === 'all' || 
      (filterEnabled === 'enabled' && flag.enabled) ||
      (filterEnabled === 'disabled' && !flag.enabled);
    return matchesSearch && matchesScope && matchesEnabled;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const fetchSystemHealth = async () => {
    setHealthLoading(true);
    setHealthError(null);
    try {
      const response = await api.get('/admin/system-health');
      const data = await response.json();
      if (data.success) { setSystemHealth(data); } else { setHealthError(data.error || 'Failed to load system health'); }
    } catch {
      setHealthError('Failed to load system health');
    } finally {
      setHealthLoading(false); setHealthLoaded(true);
    }
  };

  useEffect(() => {
    if (subTab === 3 && !healthLoaded) { fetchSystemHealth(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTab]);

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        System Management
      </Typography>
      
      {/* Sub-tabs (hidden when this component is used as a standalone locked view) */}
      {lockedView !== 'flags' && (
        <Tabs
          value={subTab}
          onChange={(_, newValue) => setSubTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label="Knowledge Base" value={1} />
          <Tab label="Audit Log" value={2} />
          <Tab label="System Health" value={3} />
        </Tabs>
      )}

      {/* Feature Flags Sub-tab */}
      <SubTabPanel value={subTab} index={0}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search flags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Scope</InputLabel>
            <Select
              value={filterScope}
              label="Scope"
              onChange={(e) => setFilterScope(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="global">Global</MenuItem>
              <MenuItem value="tenant">Tenant</MenuItem>
              <MenuItem value="user">User</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterEnabled}
              label="Status"
              onChange={(e) => setFilterEnabled(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="enabled">Enabled</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
            </Select>
          </FormControl>

          <IconButton onClick={fetchFlags} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Chip
            icon={<EnabledIcon />}
            label={`${flags.filter(f => f.enabled).length} Enabled`}
            color="success"
            variant="outlined"
          />
          <Chip
            icon={<DisabledIcon />}
            label={`${flags.filter(f => !f.enabled).length} Disabled`}
            color="error"
            variant="outlined"
          />
          <Chip
            label={`${flags.length} Total`}
            variant="outlined"
          />
        </Box>

        {/* Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Flag Key</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Scope</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Last Updated</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Updated By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredFlags.map((flag) => (
                  <TableRow
                    key={flag.flag_key}
                    hover
                    sx={{
                      bgcolor: flag.enabled ? 'success.50' : 'inherit',
                      '&:hover': {
                        bgcolor: flag.enabled ? 'success.100' : 'grey.50',
                      },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontWeight={500}>
                        {flag.flag_key}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {flag.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={flag.scope}
                        size="small"
                        color={flag.scope === 'global' ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={flag.enabled}
                        onChange={() => handleToggleClick(flag)}
                        color="success"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(flag.updated_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {flag.updated_by || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SubTabPanel>

      {/* Knowledge Base Sub-tab */}
      <SubTabPanel value={subTab} index={1}>
        <Alert severity="info">
          Knowledge Base management coming soon. This will allow you to manage AI admin chat documentation.
        </Alert>
      </SubTabPanel>

      {/* Audit Log Sub-tab */}
      <SubTabPanel value={subTab} index={2}>
        <Alert severity="info">
          Audit log coming soon. View all admin actions and system changes.
        </Alert>
      </SubTabPanel>

      {/* System Health Sub-tab */}
      <SubTabPanel value={subTab} index={3}>
        {healthLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress sx={{ color: "#2D6E6A" }} /></Box>
        ) : healthError ? (
          <Alert severity="error">{healthError}</Alert>
        ) : !systemHealth ? (
          <Alert severity="info">Loading system health…</Alert>
        ) : (
          <Box>
            <Alert severity={systemHealth.status === "operational" ? "success" : systemHealth.status === "degraded" ? "warning" : "error"} sx={{ mb: 2 }}>
              Platform status: <strong>{String(systemHealth.status || "unknown").toUpperCase()}</strong>
              {systemHealth.lastChecked ? "  ·  checked " + new Date(systemHealth.lastChecked).toLocaleTimeString() : ""}
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Live platform health from CloudWatch over the last hour — Lambda reliability, API Gateway errors and latency, and active alarms. Overall status reflects the worst signal.
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2, mb: 3 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">Lambda error rate (1h)</Typography>
                <Typography variant="h5" fontWeight={700}>{systemHealth.metrics?.lambdaErrorRate != null ? (systemHealth.metrics.lambdaErrorRate * 100).toFixed(2) + "%" : "—"}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>Errors ÷ invocations across key functions</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">API 5xx (1h)</Typography>
                <Typography variant="h5" fontWeight={700}>{systemHealth.metrics?.apiGw5xx != null ? systemHealth.metrics.apiGw5xx : "—"}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>Server (5xx) errors returned by the API gateway</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">API latency p99</Typography>
                <Typography variant="h5" fontWeight={700}>{systemHealth.metrics?.latencyP99 != null ? systemHealth.metrics.latencyP99 + "ms" : "—"}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>99th-percentile API response time</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">Alarms firing</Typography>
                <Typography variant="h5" fontWeight={700}>{systemHealth.metrics?.alarmsInAlarm != null ? systemHealth.metrics.alarmsInAlarm : "—"}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>CloudWatch alarms currently in ALARM</Typography>
              </Paper>
            </Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Per-function (last 1h)</Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Function</TableCell>
                  <TableCell align="right">Invocations</TableCell>
                  <TableCell align="right">Errors</TableCell>
                  <TableCell align="right">Error rate</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {(systemHealth.perFunction || []).length === 0 ? (
                    <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary">No invocation data in the window.</Typography></TableCell></TableRow>
                  ) : (systemHealth.perFunction || []).map((f: any) => (
                    <TableRow key={f.name}>
                      <TableCell>{f.name.replace("my-travel-agent-", "")}</TableCell>
                      <TableCell align="right">{f.invocations}</TableCell>
                      <TableCell align="right">{f.errors}</TableCell>
                      <TableCell align="right">
                        <Chip size="small" label={(f.errorRate * 100).toFixed(1) + "%"}
                          color={f.errorRate > 0.01 ? "error" : "default"} variant={f.errorRate > 0.01 ? "filled" : "outlined"} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {(systemHealth.checks || []).some((c: any) => c.detail && c.detail.includes("unavailable")) && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                Some metrics are temporarily unavailable; status falls back to the checks that did report.
              </Typography>
            )}
          </Box>
        )}
      </SubTabPanel>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, flag: null, newValue: false })}>
        <DialogTitle>
          {confirmDialog.newValue ? 'Enable' : 'Disable'} Feature Flag
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {confirmDialog.newValue ? 'enable' : 'disable'}{' '}
            <strong>{confirmDialog.flag?.flag_key}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {confirmDialog.flag?.description}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, flag: null, newValue: false })}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmToggle}
            variant="contained"
            color={confirmDialog.newValue ? 'success' : 'error'}
            disabled={updating}
          >
            {updating ? <CircularProgress size={20} /> : (confirmDialog.newValue ? 'Enable' : 'Disable')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SystemManagementTab;
